import React, { useMemo, useState } from 'react';
import { AutoComplete, Button, Flex, InputNumber, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChannelImportRow } from '../../../domain/entities/ChannelImportDraft';
import {
  MAX_CHANNEL_SENDING_INTERVAL_MINUTES,
  MIN_CHANNEL_SENDING_INTERVAL_MINUTES,
} from '../../../domain/services/ChannelAdministrationRules';
import { ChannelType } from '../../../shared/types/Channel';

import './ChannelImportReviewTable.styles.css';

type ImportableType = ChannelImportRow['type'];

interface ChannelImportReviewTableProps {
  rows: ChannelImportRow[];
  categoryNames: string[];
  disabled: boolean;
  onChange: (rows: ChannelImportRow[]) => void;
}

const shortLink = (link: string) => link.replace(/^https:\/\/t\.me\//, '');

const ChannelImportReviewTable: React.FC<ChannelImportReviewTableProps> = ({ rows, categoryNames, disabled, onChange }) => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkInterval, setBulkInterval] = useState<number | null>(null);

  const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const categoryOptions = useMemo(() => categoryNames.map((name) => ({ value: name })), [categoryNames]);
  const typeOptions = [
    { value: ChannelType.FREE, label: t('channel-type-free', { ns: 'labels' }) },
    { value: ChannelType.PAID, label: t('channel-type-paid', { ns: 'labels' }) },
  ];

  const updateRow = (link: string, patch: Partial<ChannelImportRow>) =>
    onChange(rows.map((row) => (row.link === link ? { ...row, ...patch } : row)));

  const updateSelected = (patch: Partial<ChannelImportRow>) =>
    onChange(rows.map((row) => (selected.includes(row.link) ? { ...row, ...patch } : row)));

  const removeLinks = (links: string[]) => {
    onChange(rows.filter((row) => !links.includes(row.link)));
    setSelected((current) => current.filter((link) => !links.includes(link)));
  };

  const columns: ColumnsType<ChannelImportRow> = [
    {
      title: t('channel-link', { ns: 'labels' }),
      render: (_, row) => (
        <Flex vertical gap={2}>
          <Space size={6} wrap>
            <a href={row.link} target="_blank" rel="noreferrer">{shortLink(row.link)}</a>
            <Tag color={row.exists ? 'blue' : 'green'}>
              {row.exists ? t('channel-import-row-update', { ns: 'labels' }) : t('channel-import-row-new', { ns: 'labels' })}
            </Tag>
          </Space>
          {row.title && <Typography.Text type="secondary" className="channel-import-title">{row.title}</Typography.Text>}
        </Flex>
      ),
    },
    {
      title: t('channel-type', { ns: 'labels' }),
      width: 170,
      render: (_, row) => (
        <Space size={4}>
          <Select<ImportableType>
            size="small"
            value={row.type}
            options={typeOptions}
            disabled={disabled}
            onChange={(type) => updateRow(row.link, { type, typeResolved: true })}
            className="channel-import-type"
          />
          {!row.typeResolved && (
            <Tooltip title={t('channel-import-type-unresolved', { ns: 'labels' })}>
              <WarningOutlined className="channel-import-warning" />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('channel-category', { ns: 'labels' }),
      width: 200,
      render: (_, row) => (
        <AutoComplete
          size="small"
          value={row.categoryName}
          options={categoryOptions}
          disabled={disabled}
          filterOption={(input, option) => (option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
          placeholder={t('channel-no-category', { ns: 'labels' })}
          onChange={(categoryName: string) => updateRow(row.link, { categoryName })}
          className="channel-import-category"
        />
      ),
    },
    {
      title: t('channel-members-count', { ns: 'labels' }),
      width: 140,
      align: 'right',
      render: (_, row) => (
        <InputNumber
          size="small"
          min={0}
          precision={0}
          value={row.membersCount}
          disabled={disabled}
          formatter={(value) => (value === undefined ? '' : numberFormat.format(Number(value)))}
          parser={(value) => Number((value ?? '').replace(/\D/g, '') || 0)}
          onChange={(value) => updateRow(row.link, { membersCount: value ?? 0 })}
        />
      ),
    },
    {
      title: t('channel-sending-interval', { ns: 'labels' }),
      width: 150,
      render: (_, row) => (
        <InputNumber
          size="small"
          min={MIN_CHANNEL_SENDING_INTERVAL_MINUTES}
          max={MAX_CHANNEL_SENDING_INTERVAL_MINUTES}
          precision={0}
          value={row.minimalSendingInterval}
          disabled={disabled}
          addonAfter={t('schedule-interval-minutes', { ns: 'labels' })}
          onChange={(value) => updateRow(row.link, { minimalSendingInterval: value ?? MIN_CHANNEL_SENDING_INTERVAL_MINUTES })}
        />
      ),
    },
    {
      title: t('channel-is-private', { ns: 'labels' }),
      width: 110,
      align: 'center',
      render: (_, row) => (
        <Switch size="small" checked={row.isPrivate} disabled={disabled} onChange={(isPrivate) => updateRow(row.link, { isPrivate })} />
      ),
    },
    {
      width: 56,
      render: (_, row) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          disabled={disabled}
          aria-label={t('channel-import-remove-row', { ns: 'buttons' })}
          onClick={() => removeLinks([row.link])}
        />
      ),
    },
  ];

  return (
    <Flex vertical gap={12}>
      {selected.length > 0 && !disabled && (
        <Flex className="channel-import-bulk" gap={8} wrap="wrap" align="center">
          <Typography.Text strong>{t('selected-count', { ns: 'labels', count: selected.length })}</Typography.Text>
          <Select<ImportableType>
            size="small"
            placeholder={t('channel-type', { ns: 'labels' })}
            options={typeOptions}
            onChange={(type) => updateSelected({ type, typeResolved: true })}
            className="channel-import-type"
          />
          <Space.Compact size="small">
            <AutoComplete
              value={bulkCategory}
              options={categoryOptions}
              placeholder={t('channel-category', { ns: 'labels' })}
              onChange={setBulkCategory}
              className="channel-import-category"
            />
            <Button onClick={() => updateSelected({ categoryName: bulkCategory.trim() })}>{t('apply-to-selected', { ns: 'buttons' })}</Button>
          </Space.Compact>
          <Space.Compact size="small">
            <InputNumber
              min={MIN_CHANNEL_SENDING_INTERVAL_MINUTES}
              max={MAX_CHANNEL_SENDING_INTERVAL_MINUTES}
              precision={0}
              value={bulkInterval}
              placeholder={t('channel-sending-interval', { ns: 'labels' })}
              onChange={setBulkInterval}
            />
            <Button disabled={!bulkInterval} onClick={() => bulkInterval && updateSelected({ minimalSendingInterval: bulkInterval })}>
              {t('apply-to-selected', { ns: 'buttons' })}
            </Button>
          </Space.Compact>
          <Button size="small" danger onClick={() => removeLinks(selected)}>{t('channel-import-remove-selected', { ns: 'buttons' })}</Button>
        </Flex>
      )}

      <Table<ChannelImportRow>
        size="small"
        rowKey={(row) => row.link}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1100 }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
        rowSelection={disabled ? undefined : {
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys as string[]),
        }}
        rowClassName={(row) => (row.typeResolved ? '' : 'channel-import-row-review')}
        locale={{ emptyText: t('no-items', { ns: 'labels' }) }}
      />
    </Flex>
  );
};

export default ChannelImportReviewTable;
